/** The dismissal sources the chrome can distinguish. Exported so a consumer never spells one. */
export const DISMISSAL_ESCAPE = 'escape';
export const DISMISSAL_BACKDROP = 'backdrop';
export const DISMISSAL_CLOSE_BUTTON = 'closeButton';

/**
 * The open-dialog STACK — who owns Escape, and who reclaims focus that fell out of a dialog.
 *
 * MODALCHROME-BURNDOWN-001 Q-L2-1 / Q-L2-2 (owner ruling, 2026-09-20). Two defects found by
 * migrating `<PaletteShell>` onto `useModalChrome`, both pre-existing and both fleet-wide:
 *
 * ## 1. Escape reached the host page
 *
 * The chrome handles Escape on `document` — deliberately, because the pos-web copy it replaced
 * bound to the container and so did nothing until the user had tabbed in. But an event cannot both
 * be handled at `document` and be withheld from `document`: a host listener registered BEFORE the
 * dialog's fired too, because `stopPropagation()` does not reach same-node listeners. Measured on
 * `<PaletteShell>`, whose own contract test had asserted the opposite for a year — it held only
 * while the handler was a React `onKeyDown` (React attaches at the root container, BELOW document).
 *
 * In the product that is: a command palette opened over a drawer, Escape, and both close.
 *
 * 🔴 **`stopImmediatePropagation` alone is the wrong fix, and that is why this module exists.**
 * Same-node listeners run in REGISTRATION order, so the winner would be whichever dialog opened
 * FIRST — the OUTER one. Escape would close the drawer and leave the palette on top of nothing.
 * Wrong in the opposite direction, and silent.
 *
 * The fix is ONE listener for the whole fleet, in the CAPTURE phase, that asks the stack who is on
 * top. Capture on `document` runs before every bubble listener beneath it, so the top dialog can
 * genuinely own the key.
 *
 * ## 2. A dialog whose focused element UNMOUNTS loses its trap for good
 *
 * The Tab cycle binds on the dialog's container. Once focus is outside that container the listener
 * is not on the event's path at all, so it cannot run — including to pull focus back. Normally
 * unreachable (the chrome moves focus in on open, and a click outside hits the backdrop), but a
 * focused element that UNMOUNTS drops `document.activeElement` to `<body>`, and from that moment
 * the dialog has no trap for the rest of its life. A result row that re-renders is enough.
 *
 * Two local traps this hook replaced defended against it with a third Tab arm. They could, because
 * they bound on `document`; with per-dialog document listeners that arm reintroduces exactly the
 * conflict above. Owned centrally it is safe: only the TOP dialog reclaims.
 *
 * ## Why registration order is the right definition of "top"
 *
 * Not DOM order: `<Modal>` and `<AlertDialog>` portal to `document.body`, so a dialog opened from
 * inside another is a SIBLING in the DOM, not a descendant, and `contains()` would call neither one
 * inner. Registration order is mount order, and the dialog that opened last is the one the user is
 * looking at.
 *
 * @module @puffin-core/dialog-chrome/dialogStack
 */

/**
 * Open dialogs, oldest first. The LAST entry is the top — the one the user is interacting with.
 * Module scope on purpose: the stack has to be shared by every dialog in the app, and a React
 * context could not reach a dialog rendered through a portal from another tree.
 */
const stack = [];

/**
 * The single shared listener pair, attached EXACTLY while the stack is non-empty: `registerDialog`
 * pushes then attaches, the last release detaches, and detaching cancels a pending reclaim. So
 * every handler below runs with a top entry, and none of them guards for an empty stack — a guard
 * there would be a branch no event can reach.
 */
let listenersAttached = false;

/** @returns {Object} The top entry. Only called by a handler, so the stack is non-empty. */
function top() {
  return stack[stack.length - 1];
}

/**
 * Escape, owned by the top dialog alone.
 *
 * Capture phase, so this runs before any bubble listener on `document` or below it — including a
 * host page's. `stopImmediatePropagation()` is safe HERE and only here: there is exactly one of
 * these listeners no matter how many dialogs are open, so it cannot silence a sibling dialog.
 *
 * ⚠️ The key is swallowed even when the top dialog is NOT dismissible. That is the modal contract,
 * not an oversight: a dialog that refuses Escape must not let the key fall through and close
 * something behind it. `closeOnEscape: false` means "nothing happens", never "someone else acts".
 *
 * @param {KeyboardEvent} event
 * @returns {void}
 */
function handleEscapeCapture(event) {
  if (event.key !== 'Escape') return;
  const entry = top();

  // `stopPropagation()` in the CAPTURE phase is what stops the host's bubble listeners: capture on
  // `document` runs before the event ever reaches its target, so nothing below sees it.
  //
  // `stopImmediatePropagation()` covers the remaining case and ONLY that case: a host listener on
  // `document` in CAPTURE phase, registered AFTER this one — a component mounting behind an
  // already-open dialog. It cannot help against a capture listener registered EARLIER, because
  // same-node listeners run in registration order and that one simply runs first. Measured both
  // ways while writing `dialogStack.test.jsx`; the boundary is stated rather than assumed, which
  // is the difference between a guarantee and a hope.
  event.stopPropagation();
  event.stopImmediatePropagation();

  if (!entry.closeOnEscape) return;
  // Guarded because a non-dismissible dialog legitimately passes no `onClose`, and React 19 no
  // longer validates propTypes at runtime, so this is the only place the mistake can be caught.
  // The chrome tells the consumer HOW it was dismissed. Nearly every surface ignores the
  // argument, and should; two pos-web AUTH surfaces write it to an audit trail, and a
  // source-blind `onClose()` would have collapsed 'escape_key' and 'backdrop_click' into one
  // value on an identity-verification and an elevated-authorisation dialog.
  if (typeof entry.onClose === 'function') entry.onClose(DISMISSAL_ESCAPE);
}

/**
 * The pending reclaim's timer, or null. ONE at a time: a burst of `focusout`s (a re-render that
 * removes several nodes) needs one verdict, and `detachListeners` cancels it so a reclaim queued
 * for a dialog that has since closed never acts on whatever stack exists when it fires.
 */
let pendingReclaim = null;

/**
 * Reclaim focus that fell out of the top dialog.
 *
 * Narrow ON PURPOSE. It acts only when focus has been LOST — the focused element was removed and
 * the browser had nowhere to put it, so it settled on `<body>`. Any other destination is left
 * alone: a consumer that deliberately moves focus to a portal, a toast, or a nested widget is
 * doing something legitimate, and a handler that fought every outward move would be a focus war
 * rather than a trap.
 *
 * Two rules decide "lost", and both come from the browser's real event order — which jsdom does
 * not reproduce (it moves focus synchronously inside `.focus()`):
 *
 * 1. **A `relatedTarget` means focus is MOVING to a real element: never act.** A click or Tab from
 *    one field to another fires `focusout(relatedTarget = next)` BEFORE the next element is
 *    focused, so at that instant `document.activeElement` reads `<body>` on EVERY ordinary move.
 *
 * 2. **With no `relatedTarget`, judge only after a MACROTASK.** A microtask is still too early.
 *    Measured in headless Chromium on a bare page with two inputs, a click or Tab from a to b
 *    gives `focusout relatedTarget=b` → `microtask active=BODY` → `setTimeout(0) active=INPUT#b`.
 *    So when the browser does not name the destination, only a macrotask reads where focus
 *    really settled.
 *
 * 🔴 This handler used to queue a MICROTASK on every `focusout` and focus the container whenever
 * `activeElement` was `<body>` (2026-09-20 → 2026-10-11, LIFE-SHORTLISTED-001). Per rule 1 that is
 * every move, so in a real browser no click or Tab could reach a second field in ANY dialog: focus
 * was pulled back to the container each time. Found live on console.lifeshortlisted.com's
 * password-reset dialog, whose "Confirm New Password" field could not be focused. The jsdom suite
 * was green throughout; the regression proof that can see this class runs in a real browser.
 *
 * @param {FocusEvent} event
 * @returns {void}
 */
function handleFocusOut(event) {
  if (event.relatedTarget) return;
  if (pendingReclaim !== null) return;
  pendingReclaim = setTimeout(() => {
    pendingReclaim = null;
    const container = top().getContainer();
    if (!container || !container.isConnected) return;
    if (document.activeElement !== document.body) return;
    if (typeof container.focus === 'function') container.focus();
  }, 0);
}

/** @returns {void} */
function attachListeners() {
  if (listenersAttached) return;
  document.addEventListener('keydown', handleEscapeCapture, true);
  document.addEventListener('focusout', handleFocusOut);
  listenersAttached = true;
}

/** @returns {void} */
function detachListeners() {
  if (!listenersAttached) return;
  document.removeEventListener('keydown', handleEscapeCapture, true);
  document.removeEventListener('focusout', handleFocusOut);
  if (pendingReclaim !== null) {
    clearTimeout(pendingReclaim);
    pendingReclaim = null;
  }
  listenersAttached = false;
}

/**
 * Register an open dialog and return its unregister function.
 *
 * @param {{onClose: Function|undefined, closeOnEscape: boolean, getContainer: () => (HTMLElement|null)}} entry
 * @returns {() => void} Unregister. Idempotent — a double call is a no-op rather than popping
 *   someone else's entry, because React can run an effect cleanup more than once in StrictMode.
 * @throws {TypeError} When `getContainer` is not a function — a stack entry that cannot answer
 *   "where am I" is unusable, and failing at registration names the caller instead of surfacing as
 *   a focus bug later.
 */
export function registerDialog(entry) {
  if (typeof entry.getContainer !== 'function') {
    throw new TypeError('registerDialog requires a getContainer function');
  }
  stack.push(entry);
  attachListeners();

  let released = false;
  return () => {
    if (released) return;
    released = true;
    const index = stack.lastIndexOf(entry);
    // Spliced by IDENTITY, never popped: dialogs do not necessarily close in the order they
    // opened, and popping would evict whoever happened to be on top.
    if (index !== -1) stack.splice(index, 1);
    if (stack.length === 0) detachListeners();
  };
}

/**
 * Test seam. The stack is module state, so a suite that renders dialogs without unmounting them
 * would leak entries into the next test and the failure would appear somewhere else entirely.
 *
 * @returns {void}
 */
export function __resetDialogStackForTests() {
  stack.length = 0;
  detachListeners();
}
